import React, { useState, useEffect, useCallback } from "react";
import { Typography, Box, Paper } from "@mui/material";
import Settings from "./Settings";
import RoomTable from "./RoomTable";
import Summary from "./Summary";
import PdfForm from "./PdfForm";
import { calculateProducts } from "./calculationLogic";
import { waitForPrices, getPriceCategoryForApp } from "./utils/blobStorage";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const theme = createTheme({
  components: {
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
  },
});

function App() {
  const [settingsData, setSettingsData] = useState({});
  const [roomTableData, setRoomTableData] = useState({ rows: [] });
  const [summaryData, setSummaryData] = useState({
    recuperators: [],
    materials: [],
  });
  const [selectedRecuperator, setSelectedRecuperator] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const priceCategory = getPriceCategoryForApp();

  const updateSettingsData = useCallback((newSettingsData) => {
    setSettingsData(newSettingsData);
    triggerRecalculation(newSettingsData, roomTableData);
  }, [roomTableData]);

  const updateRoomTableData = useCallback((newRoomTableData) => {
    setRoomTableData(newRoomTableData);
    triggerRecalculation(settingsData, newRoomTableData);
  }, [settingsData]);

  const triggerRecalculation = (settingsData, roomTableData) => {
    const newProducts = calculateProducts(settingsData, roomTableData);

    setSummaryData({
      ...newProducts,
    });
  };

  const handleSubmissionSuccess = () => {
    setIsSubmitted(true);
  };

  const updateHeight = () => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ height }, process.env.REACT_APP_PARENT_DOMAIN);
  };

  // Load prices in background on mount
  useEffect(() => {
    waitForPrices().then(() => {
      console.debug('Prices loaded');
    }).catch((error) => {
      console.warn('Failed to load prices:', error);
    });
  }, []);

  // Auto-select first recuperator when recuperators list changes
  useEffect(() => {
    if (summaryData.recuperators.length > 0) {
      setSelectedRecuperator(summaryData.recuperators[0]);
    } else {
      setSelectedRecuperator(null);
    }
  }, [summaryData.recuperators]);

  useEffect(() => {
    updateHeight();

    const observer = new MutationObserver(updateHeight);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {isSubmitted ? (
        <p style={{ textAlign: "center", fontSize: "18px", color: "green" }}>
          Jūsų duomenys sėkmingai išsiųsti nurodytu el. paštu.
        </p>
      ) : (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Settings updateSettingsData={updateSettingsData} />
          <RoomTable updateRoomTableData={updateRoomTableData} />
          <Summary 
            summaryData={summaryData}
            selectedRecuperator={selectedRecuperator}
            setSelectedRecuperator={setSelectedRecuperator}
            priceCategory={priceCategory}
          />
          <PdfForm
            calculatorData={{
              settingsData,
              roomTableData,
              summaryData,
              selectedRecuperator,
              priceCategory,
            }}
            onSuccess={handleSubmissionSuccess}
          />
          <Box 
            sx={{
              p: 2,
              pt: 0,
              "@media (max-width: 600px)": {
                p: 1,
                pt: 0,
              },
            }}
          >
            <Paper elevation={2} sx={{ 
              p: 2,
              "@media (max-width: 600px)": {
                p: 1,
              },
            }}>
              <Typography variant="body1">
                Skaičiuoklė sugeneruoja reikiamus rezultatus, tik įvedus bent po
                vieną patalpą oro padavimui ir oro ištraukimui.
              </Typography>
              <Typography variant="body1" mt={2}>
                Skaičiuoklės generuojami oro kiekiai parenkami su papildoma
                atsarga, pagal statybos techninį reglamentą „STR 2.09.02:2005
                Šildymas, vėdinimas ir oro kondicionavimas“, ir užtikrina
                aukštesnę oro švarumo kategoriją.
              </Typography>
              <Typography variant="body1" mt={2}>
                Sugeneruoti medžiagų kiekiai ir modeliai yra orientaciniai, ir
                negali būti naudojami kaip projektiniai. Dėl tikslesnių
                projektinių įrangos ir dalių kiekių prašome kreiptis į mūsų
                specialistus.
              </Typography>
            </Paper>
          </Box>
        </Box>
      )}
    </ThemeProvider>
  );
}

export default App;
